import { toApiDateTime, toLuxon } from "@/lib/timeUtils";
import {
  createShift as apiCreateShift,
  updateShift as apiUpdateShift,
  deleteShift as apiDeleteShift,
  checkShiftConflicts as apiCheckShiftConflicts,
} from "../lib/api";

export function useShifts() {

  /**
   * Check if a shift conflicts with existing shifts
   * @param {Object} shift - The shift to check
   * @param {number} excludeShiftId - Optional shift ID to exclude (for edits)
   * @returns {Promise<Object>} - Object with conflict information
   */
  const checkShiftConflicts = async (shift, excludeShiftId = null) => {
    try {
      if (!shift || !shift.userId || !shift.startTime || !shift.endTime) {
        return { has_conflicts: false, conflicts: [] };
      }

      const params = {
        user_id: shift.userId,
        start_time: toApiDateTime(shift.startTime),
        end_time: toApiDateTime(shift.endTime),
      };

      if (excludeShiftId) {
        params.exclude_shift_id = excludeShiftId;
      }

      const response = await apiCheckShiftConflicts(params);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Determine if a shift is a duplicate of an existing shift
   * @param {Object} shift - The shift to check
   * @param {Array} existingShifts - Array of existing shifts
   * @returns {Object|null} - Returns conflicting shift or null
   */
  const isDuplicateShift = (shift, existingShifts) => {
    // Input validation
    if (!shift || !existingShifts) {
      return null;
    }

    // Make sure existingShifts is an array
    if (!Array.isArray(existingShifts)) {
      return null;
    }

    try {
      // Check for exact duplicates - same user, same day, same time
      return (
        existingShifts.find((existing) => {
          if (!existing) return false;

          // Safely extract start/end times
          let existingStart, existingEnd, shiftStart, shiftEnd;
          try {
            existingStart = toLuxon(existing.start);
            existingEnd = toLuxon(existing.end);
            shiftStart = toLuxon(shift.startTime);
            shiftEnd = toLuxon(shift.endTime);
          } catch (e) {
            return false; // Skip if date parsing fails
          }

          // Skip comparing to the same shift when editing
          if (shift.id && existing.id === shift.id) return false;

          // Get user ID safely
          const existingUserId =
            existing.extendedProps?.userId || existing.userId;
          const shiftUserId = shift.userId;

          // Check if it's the same user
          const sameUser = existingUserId === shiftUserId;
          if (!sameUser) return false;

          // Check if it's the same time period (within a 5 minute tolerance)
          const startDiff = Math.abs(existingStart - shiftStart);
          const endDiff = Math.abs(existingEnd - shiftEnd);

          // Consider it a duplicate if start and end times are within 5 minutes of each other
          const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
          return startDiff < fiveMinutes && endDiff < fiveMinutes;
        }) || null
      );
    } catch (error) {
      console.error("Error checking for duplicate shift:", error);
      return null;
    }
  };

  /**
   * Save a new shift with conflict detection
   */
  const createShift = async (shift, options = {}) => {
    const { skipConflictCheck = false, skipDuplicateCheck = false, existingShifts = [] } = options;

    try {
      // Step 1: Check for duplicates if not skipped
      if (
        !skipDuplicateCheck &&
        Array.isArray(existingShifts) &&
        existingShifts.length > 0
      ) {
        const duplicate = isDuplicateShift(shift, existingShifts);
        if (duplicate) {
          return {
            success: false,
            error: "duplicate",
            message: "This appears to be a duplicate shift",
            shift: duplicate,
          };
        }
      }

      // Step 2: Check for conflicts if not skipped
      if (!skipConflictCheck) {
        const conflicts = await checkShiftConflicts(shift);
        if (conflicts && conflicts.has_conflicts) {
          return {
            success: false,
            error: "conflict",
            message: "This shift conflicts with existing shifts",
            conflicts: conflicts.conflicts,
          };
        }
      }

      // Step 3: Create the shift
      const payload = {
        user_id: shift.userId,
        store_id: shift.storeId,
        start_time: toApiDateTime(shift.startTime),
        end_time: toApiDateTime(shift.endTime),
        notes: shift.notes || "",
      };

      const response = await apiCreateShift(payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      throw err;
    }
  };

  /**
   * Update an existing shift
   */
  const updateShift = async (id, shift, options = {}) => {
    const { skipConflictCheck = false, skipDuplicateCheck = false, existingShifts = [] } = options;

    try {
      // Step 1: Check for duplicates if not skipped
      if (
        !skipDuplicateCheck &&
        Array.isArray(existingShifts) &&
        existingShifts.length > 0
      ) {
        const duplicate = isDuplicateShift({ ...shift, id }, existingShifts);
        if (duplicate) {
          return {
            success: false,
            error: "duplicate",
            message: "This appears to be a duplicate shift",
            shift: duplicate,
          };
        }
      }

      // Step 2: Check for conflicts if not skipped
      if (!skipConflictCheck) {
        const conflicts = await checkShiftConflicts(shift, id);
        if (conflicts && conflicts.has_conflicts) {
          return {
            success: false,
            error: "conflict",
            message: "This shift conflicts with existing shifts",
            conflicts: conflicts.conflicts,
          };
        }
      }

      // Step 3: Update the shift
      const payload = {
        user_id: shift.userId,
        store_id: shift.storeId,
        start_time: toApiDateTime(shift.startTime),
        end_time: toApiDateTime(shift.endTime),
        notes: shift.notes || "",
      };

      const response = await apiUpdateShift(id, payload);
      return {
        success: true,
        data: response.data,
      };
    } catch (err) {
      throw err;
    }
  };

  /**
   * Delete a shift
   */
  const deleteShift = async (id) => {
    try {
      await apiDeleteShift(id);
      return true;
    } catch (err) {
      throw err;
    }
  };

  /**
   * Save a shift (create or update based on id presence)
   * Unified API for Calendar.vue compatibility
   */
  const saveShift = async (shift, options = {}) => {
    if (shift.id) {
      return await updateShift(shift.id, shift, options);
    } else {
      return await createShift(shift, options);
    }
  };

  return {
    createShift,
    updateShift,
    saveShift,
    deleteShift,
    checkShiftConflicts,
    isDuplicateShift,
  };
}
